import React, { Component } from 'react';
import { CreditCard, DnDItem } from 'app-types';
import classNames from 'classnames';
import { ReactStripeElements } from 'react-stripe-elements';

import { isEqual, differenceWith } from 'lodash-es';
import styles from './styles.module.scss';
import { Button } from 'components/antd';
import { ThemeContext } from 'app-themes';
import DynamicFields from 'components/DynamicFields/DynamicFields';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import CreditCardRow from './CreditCardRow';
import CreditCardsEmpty from './CreditCardsEmpty';
import CreditCardForm from './CreditCardForm';
import { toJS } from 'mobx';
import { FetchStatus } from 'servicesCommon/apiTypes';

interface State {
	isAdding?: boolean;
}
interface Props {
	dndItems: DnDItem[];
	activeCreditCards: CreditCard[];
	deletedCreditCards: CreditCard[];
	expiredCreditCards: CreditCard[];
	fetchStatus?: FetchStatus;
	saveCreditCard: (
		stripe: ReactStripeElements.StripeProps,
		cardElement: stripe.elements.Element
	) => void;
	updateActiveCreditCards: (
		items: DnDItem[],
		removedRowNumber?: number,
		removedExpiredCard?: CreditCard
	) => void;
}

export default class CreditCards extends Component<Props, State> {
	static contextType = ThemeContext;

	state = { isAdding: false };

	reorder(list: any[], startIndex: number, endIndex: number) {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		return result;
	}

	onDragEnd = (result: DropResult) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		}
		const { dndItems } = this.props;

		const newItems = this.reorder(dndItems, result.source.index, result.destination.index);

		this.props.updateActiveCreditCards(newItems);
	};

	updateLocationsAmount = (listRange: number[]) => {
		// in case of removing item
		const newItems = listRange.map(item => {
			return {
				rowInitialNumber: item,
			};
		});

		const removedItem = differenceWith(this.props.dndItems, newItems, isEqual)[0];

		this.props.updateActiveCreditCards(newItems, removedItem.rowInitialNumber);
	};

	hideCreditCard = () => {
		this.setState({
			isAdding: false,
		});
	};

	render() {
		const {
			activeCreditCards,
			deletedCreditCards,
			expiredCreditCards,
			dndItems,
			fetchStatus,
		} = this.props;
		return (
			<>
				<div className="modalBoxRow">
					<div className="modalBoxCol w12">
						<div className={styles.category}>
							<h4 className={styles.categoryTitle}>Active cards</h4>
							<div className={styles.categoryInfo}>
								Adding and removing credit cards are saved immediately and cannot be
								undone
							</div>
						</div>

						{activeCreditCards.length === 0 && expiredCreditCards.length === 0 ? (
							<CreditCardsEmpty text="No cards yet" />
						) : (
							<>
								<DragDropContext onDragEnd={this.onDragEnd}>
									<Droppable droppableId={`ccDrop`}>
										{provided => (
											<div
												{...provided.droppableProps}
												ref={provided.innerRef}
											>
												<DynamicFields
													listName={'bookingLocations'}
													initialValues={toJS(dndItems)}
													updateRangeCallback={this.updateLocationsAmount}
													min={0}
													render={props => {
														const {
															rowInitialNumber,
															rowActualIndex,
															removeRow,
														} = props;

														const currentCreditCard =
															activeCreditCards[rowActualIndex];

														if (!currentCreditCard) {
															return null;
														}

														return (
															<Draggable
																key={`ccDrag-${rowInitialNumber}`}
																draggableId={`ccDrag-${rowInitialNumber}`}
																index={rowActualIndex}
															>
																{(provided, dragSnapshot) => (
																	<div
																		ref={provided.innerRef}
																		{...provided.draggableProps}
																		{...provided.dragHandleProps}
																		style={{
																			...provided
																				.draggableProps
																				.style,
																		}}
																		className={classNames(
																			styles.locationRow,
																			{
																				[styles.locationRowDragging]:
																					dragSnapshot.isDragging,
																			}
																		)}
																	>
																		<div
																			className={
																				styles.creditCardWrapper
																			}
																		>
																			<CreditCardRow
																				creditCard={
																					currentCreditCard
																				}
																				onRemoveItem={() => {
																					removeRow(
																						rowInitialNumber
																					);
																				}}
																			/>
																		</div>
																	</div>
																)}
															</Draggable>
														);
													}}
												/>
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
							</>
						)}
						{expiredCreditCards.map(item => (
							<div className={styles.creditCardWrapper} key={item.paymentMethodId}>
								<CreditCardRow
									creditCard={item}
									onRemoveItem={() => {
										this.props.updateActiveCreditCards(
											this.props.dndItems,
											undefined,
											item
										);
									}}
								/>
							</div>
						))}
					</div>
				</div>
				<div className="modalBoxDeviderSmall" />

				<CreditCardForm
					fetchStatus={fetchStatus}
					isHidden={!this.state.isAdding}
					hideCreditCard={this.hideCreditCard}
					saveCreditCard={this.props.saveCreditCard}
				/>

				<div className={styles.addLocation}>
					<Button
						onClick={() => {
							this.setState({
								isAdding: true,
							});
						}}
						type="dashed"
						disabled={this.state.isAdding}
					>
						+ Add card
					</Button>
				</div>
				{deletedCreditCards.length !== 0 && (
					<div className={styles.deletedCards}>
						<div className={styles.category}>
							<h4 className={styles.categoryTitle}>Deleted cards</h4>
							<div className={styles.categoryInfo}>
								Will be removed from the GroundScope system in 3 months after the
								last charge
							</div>
						</div>
						{[...deletedCreditCards].map(item => (
							<CreditCardRow creditCard={item} isDeleted key={item.paymentMethodId} />
						))}
					</div>
				)}
			</>
		);
	}
}
